import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { UserCheck, LogOut } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Persistent orange banner displayed at the top of the viewport whenever the
 * current session is an admin "accompaniment session" (impersonation token).
 * Shows the patient name + a prominent "Terminer la séance" button that
 * restores the admin token and navigates back to the patient's admin page.
 */
const ImpersonationBanner = () => {
  const { user, endImpersonation } = useAuth();
  const navigate = useNavigate();
  const [ending, setEnding] = useState(false);
  const [elapsed, setElapsed] = useState('');

  const impersonation = user?.impersonated_by;
  const patientId = user?.id;
  const patientName = user?.name;

  // Live timer: how long has this accompaniment session been running.
  useEffect(() => {
    if (!impersonation?.started_at) return;
    const started = new Date(impersonation.started_at).getTime();
    const tick = () => {
      const diffMs = Date.now() - started;
      if (diffMs < 0) {
        setElapsed('');
        return;
      }
      const totalMin = Math.floor(diffMs / 60000);
      const h = Math.floor(totalMin / 60);
      const m = totalMin % 60;
      setElapsed(h > 0 ? `${h}h${String(m).padStart(2, '0')}` : `${m} min`);
    };
    tick();
    const id = setInterval(tick, 30000); // refresh every 30s
    return () => clearInterval(id);
  }, [impersonation?.started_at]);

  if (!impersonation) return null;

  const handleEnd = async () => {
    if (ending) return;
    setEnding(true);
    try {
      await endImpersonation();
      toast.success('Séance terminée. Vous êtes revenu·e sur votre compte admin.');
      // Return to the patient's admin page (same user we were just working with).
      if (patientId) {
        navigate(`/admin/user/${patientId}`);
      } else {
        navigate('/admin');
      }
    } catch (err) {
      toast.error("Erreur lors de la fermeture de séance. Veuillez réessayer.");
    } finally {
      setEnding(false);
    }
  };

  return (
    <div
      className="w-full bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-md border-b border-orange-600"
      style={{ position: 'sticky', top: 0, zIndex: 50 }}
      data-testid="impersonation-banner"
    >
      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-2 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <UserCheck size={18} className="flex-shrink-0" />
          <div className="min-w-0">
            <p className="font-semibold text-sm leading-tight truncate">
              Séance en cours avec <span className="underline">{patientName || 'ce patient'}</span>
            </p>
            <p className="text-[11px] text-white/90 leading-tight truncate">
              Vos actions sont enregistrées dans l'historique du patient
              {elapsed && <> · {elapsed}</>}
              {impersonation.admin_name && <> · admin : {impersonation.admin_name}</>}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleEnd}
          disabled={ending}
          className="flex items-center justify-center gap-2 bg-white text-orange-700 hover:bg-orange-50 font-semibold text-sm px-3 py-1.5 rounded-md shadow-sm transition disabled:opacity-60 flex-shrink-0"
          data-testid="impersonation-end-btn"
        >
          <LogOut size={16} />
          {ending ? 'Fermeture…' : 'Terminer la séance'}
        </button>
      </div>
    </div>
  );
};

export default ImpersonationBanner;
